import React, { useState, useEffect } from 'react';
import { PharmacyformattedDate } from "../../utils/helper/helper";
import { useCustomDispatch } from '../../hooks';
import { sendPatientText } from '../../services/actions/patients/patientActions';
import { presetMessages } from '../../services/reducers/pharmacy/sms/smsPresetSlice';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ModalCloseIcon } from '../../assets/svg/icons';
import PresetMessageDialog from '../../screens/pharmacy/sms/PresetMessageDialog';
import * as Modals from '../../assets/js/custome'
import { useCustomSelectors } from '../../services/selectors/allSelectors';

const PatientSendTextModal = ({ showModal, setShowModal, patientData }) => {

    const dispatch = useCustomDispatch();

    const [message, setMessage] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [selectedPreset, setSelectedPreset] = useState('Select Preset Message');
    const [showPresetMessages, setShowPresetMessages] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const presetMessagesData = useSelector((state) => state?.smsPreset?.smsPreset?.presets?.data || [])
    const { profileData, pharmacyLoginData } = useCustomSelectors()
    const currentDate = new Date();

    useEffect(() => {
        // Fetch preset messages when dialog isOpens
        if (showModal.sendTextModal && presetMessagesData.length === 0) {
            dispatch(presetMessages());
        }
    }, [showModal.sendTextModal, dispatch]);

    // Add this function to reset all form fields
    const resetForm = () => {
        setMessage('');
        setPhoneNumber('');
        setSelectedPreset('Select Preset Message');
        setShowPresetMessages(false);
        setError('');
    };

    // Update handleClose to use resetForm
    const handleClose = () => {
        Modals.handleHideModal(setShowModal, showModal, 'sendTextModal');
        resetForm();
        document.body.style.overflow = 'auto';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // First check if phone number is required and empty
        if (phoneNumber.trim().length != 10) {
            setError('Please enter a valid 10-digit phone number');
            return;
        }

        // Then check if message is empty
        if (!message.trim()) {
            setError('Please enter a message');
            return;
        }

        const requestData = {
            patient_id: patientData?.patient_id,
            message: message,
        };

        if (phoneNumber) {
            requestData.phone = phoneNumber;
        }

        try {
            setLoading(true);
            const response = await dispatch(sendPatientText(requestData));

            if (response?.status === 200) {
                toast.success('Message sent successfully');
                // Reset form and close modal
                resetForm();
                handleClose();
            }
        } catch (error) {
            setError('Failed to send message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePresetMessageSelect = (selectedMessage, description) => {
        const userName = patientData?.patient_name || '';
        const pharmacistName = profileData?.name || '';

        // Replace placeholders with actual values
        let replacedMessage = selectedMessage
            ?.replace(/\[PATIENT\]/g, userName)
            ?.replace(/\[PHARMACIST\]/g, pharmacistName)
            ?.replace(/\[DATE\]/g, PharmacyformattedDate(currentDate));

        // Add pharmacy information to the message
        const formattedPhone = formatPhoneNumber(pharmacyLoginData?.phone);
        replacedMessage = `${replacedMessage}\n\n${pharmacyLoginData?.name || ''}\n${formattedPhone}`;

        setMessage(replacedMessage);
        setSelectedPreset(description);
        setShowPresetMessages(false);
    };

    // Add formatPhoneNumber helper function
    const formatPhoneNumber = (phoneNumber) => {
        // Remove all non-digits
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');

        // Format as XXX-XXX-XXXX
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

        if (match) {
            return `${match[1]}-${match[2]}-${match[3]}`;
        }

        return phoneNumber; // Return original if can't format
    };

    // When modal opens, reset form if needed
    useEffect(() => {
        if (!showModal.sendTextModal) {
            resetForm();
        }
    }, [showModal.sendTextModal]);

    useEffect(() => {
        setPhoneNumber(patientData?.phone || '');
    }, [patientData?.phone]);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity">
                    <div className="absolute inset-0 bg-gray-700 opacity-25"></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen"></span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="flex justify-between items-center px-6 py-4 border-b">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Send Message: {patientData?.name || patientData?.patient_name || 'Patient'}
                        </h3>
                        <button
                            onClick={handleClose}
                            className=" close_btn bg-tableHeader text-white rounded-full text-sm w-6 h-6 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                        >
                            <ModalCloseIcon />
                            <span className="sr-only">Close modal</span>
                        </button>
                    </div>

                    <div className="px-6 py-4">
                        <div className="space-y-4">
                            <div className="mb-4">
                                <input
                                    type="tel"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-transparent focus:ring-offset-2"
                                    placeholder="Enter 10-digit phone number"
                                    maxLength="10"
                                />
                            </div>
                            <div>
                                <div className="w-full mb-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowPresetMessages(!showPresetMessages)}
                                        className="inline-flex w-full items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-normal bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-transparent focus:ring-offset-2"
                                    >
                                        <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                        {selectedPreset}
                                    </button>
                                </div>
                                <textarea
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-transparent focus:ring-offset-2"
                                    rows="6"
                                    placeholder="Message"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                />
                            </div>
                        </div>
                        {error && (
                            <div className="mt-2 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md">
                                {error}
                            </div>
                        )}
                    </div>
                    <div className="z-30 flex items-center py-2 border-t border-gray-200 rounded-b dark:border-gray-600 sticky bottom-0 bg-light">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="rubik-500 flex justify-center mx-6 rounded items-center text-white bg-blue-700 w-full text-sm px-5 py-2.5 text-center">
                            {loading ? 'Sending...' : 'Send'}
                        </button>
                    </div>
                </div>
            </div>

            {showPresetMessages && (
                <div className='fixed top-[16%] left-0 right-0  '>
                    <PresetMessageDialog
                        isOpen={showPresetMessages}
                        onClose={() => setShowPresetMessages(false)}
                        onSelectMessage={handlePresetMessageSelect}
                        presetMessages={presetMessagesData}
                        className='absolute'
                    />
                </div>
            )}
        </div>
    );
};

export default PatientSendTextModal; 